// src/features/rooms/pages/RoomsListPage.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Avatar,
  TextField,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  MeetingRoom as MeetingRoomIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  SquareFoot as SquareFootIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useRooms } from '../hooks/useRooms';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';

import PageHeader from '../../../components/common/PageHeader';

const CLASSIFICATION_COLORS = {
  UNCLASSIFIED: 'default',
  SUPERVISED_AREA: 'warning',
  CONTROLLED_AREA: 'error',
};

const CLASSIFICATION_ICONS = {
  UNCLASSIFIED: <MeetingRoomIcon fontSize="small" />,
  SUPERVISED_AREA: <MeetingRoomIcon fontSize="small" />,
  CONTROLLED_AREA: <MeetingRoomIcon fontSize="small" />,
};

export default function RoomsListPage() {
  const { t } = useTranslation(['rooms', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // API hooks
  const {
    rooms,
    loading,
    error,
    clearError,
    fetchDepartmentRooms,
    deleteRoom,
  } = useRooms();

  // Load initial data
  useEffect(() => {
    fetchDepartmentRooms(includeInactive);
  }, [fetchDepartmentRooms, includeInactive]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchDepartmentRooms(includeInactive);
  }, [fetchDepartmentRooms, includeInactive]);

  const handleIncludeInactiveChange = useCallback((event) => {
    setIncludeInactive(event.target.checked);
  }, []);

  const handleCreateClick = useCallback(() => {
    navigate('/rooms/create');
  }, [navigate]);

  const handleViewRoom = useCallback((room) => {
    navigate(`/rooms/${room.room_id}`);
  }, [navigate]);

  const handleEditRoom = useCallback((room) => {
    navigate(`/rooms/${room.room_id}/edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, room) => {
    event.stopPropagation();
    setSelectedRoom(room);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedRoom(null);
  }, []);

  const handleDeleteRoom = useCallback((room) => {
    showConfirmDialog({
      title: t('rooms:confirm.delete_room_title'),
      message: t('rooms:confirm.delete_room_message', { name: room.name }),
      confirmText: t('rooms:confirm.delete_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteRoom(room.room_id);
          if (result.success) {
            showSuccess(t('rooms:success.room_deleted'));
          } else {
            showError(result.error || t('rooms:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('rooms:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteRoom, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('room:create');
  const canUpdate = permissions.hasScope('room:update');
  const canDelete = permissions.hasScope('room:delete');

  // Filtered rooms
  const filteredRooms = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rooms.filter(room => {
      if (!includeInactive && !room.is_active) return false;
      if (classificationFilter && room.classification !== classificationFilter) return false;
      if (term && !room.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rooms, includeInactive, searchTerm, classificationFilter]);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('rooms:grid.columns.room_name'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <MeetingRoomIcon fontSize="small" />
          </Avatar>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight="medium">
                {params.value}
              </Typography>
              {!params.row.is_active && (
                <Chip 
                  label={t('rooms:status.inactive')}
                  size="small" 
                  color="warning" 
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('common:labels.id')} <UUIDDisplay uuid={params.row.room_id} />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'classification',
      headerName: t('rooms:grid.columns.classification'),
      width: 180,
      renderCell: (params) => (
        <Chip
          icon={CLASSIFICATION_ICONS[params.value]}
          label={t(`rooms:classifications.${params.value}`)}
          size="small"
          color={CLASSIFICATION_COLORS[params.value]}
          variant="outlined"
        />
      ),
    },
    {
      field: 'area_m2',
      headerName: t('rooms:grid.columns.area'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <SquareFootIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {t('rooms:grid.area_with_unit', { area: params.value })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: t('rooms:grid.columns.status'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
          label={params.value ? t('rooms:status.active') : t('rooms:status.inactive')}
          size="small"
          color={params.value ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('rooms:grid.columns.created'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('rooms:grid.columns.unknown')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={MeetingRoomIcon}
        title={t('rooms:title')}
        subtitle={t('rooms:subtitle')}
        actions={<>
          <Tooltip title={t('rooms:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              {t('rooms:actions.add_room')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Filter Row */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('rooms:filter.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="rooms-list-page-classification-label">{t('rooms:filter.classification')}</InputLabel>
          <Select labelId="rooms-list-page-classification-label"
            value={classificationFilter}
            onChange={(e) => setClassificationFilter(e.target.value)}
            label={t('rooms:filter.classification')}
          >
            <MenuItem value="">{t('rooms:filter.all_classifications')}</MenuItem>
            {Object.keys(CLASSIFICATION_COLORS).map((key) => (
              <MenuItem key={key} value={key}>
                {t(`rooms:classifications.${key}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={includeInactive}
              onChange={handleIncludeInactiveChange}
              size="small"
            />
          }
          label={t('rooms:actions.include_inactive')}
        />
      </Box>

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={filteredRooms}
          columns={columns}
          getRowId={(row) => row.room_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewRoom(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <MeetingRoomIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('rooms:messages.no_rooms')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {includeInactive 
                    ? t('rooms:messages.no_rooms_description')
                    : t('rooms:messages.no_active_rooms')
                  }
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    {t('rooms:messages.create_first_room')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewRoom(selectedRoom); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('rooms:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && (
          <MenuItem onClick={() => { handleEditRoom(selectedRoom); handleMenuClose(); }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('rooms:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => { handleDeleteRoom(selectedRoom); handleMenuClose(); }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('rooms:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}